/**
 * SIP Peers Hooks - React Query hooks for Sip domain
 * 
 * Uses shared SIP types for type safety:
 * - SipPeerInfo: Output type (API responses)
 * - Sip.PeerCreateInput: Input type (API requests for creation)
 * - Sip.PeerUpdateInput: Input type (API requests for updates)
 * 
 * Following SST Console pattern from CORE_SST_PATTERN_IMPLEMENTATION.md (lines 600-835)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// Type-only import - safe for browser (types are erased at runtime)
import type { Sip } from '@sbclite/core';
import type { SipPeerInfo, SipPeersResponse } from '../types/sip';
import { api } from '../services/api';

export const sipPeerQueryKeys = {
  peers: ['sip-peers'] as const,
  peer: (peerId: string) => ['sip-peers', peerId] as const,
  peerEffectivePolicy: (peerId: string) => ['sip-peers', peerId, 'effective-policy'] as const,
} as const;

/**
 * SIP Peers API Response
 */
interface SipPeerResponse {
  success: boolean;
  data: SipPeerInfo;
  message?: string;
}

/**
 * Fetch all SIP peers
 */
export function useSipPeers(params?: {
  type?: 'carrier' | 'teams' | 'webrtc' | 'softphone' | 'pbx';
  enabled?: boolean;
  domain?: string;
  transport?: 'UDP' | 'TCP' | 'TLS';
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...sipPeerQueryKeys.peers, params] as const,
    queryFn: async (): Promise<SipPeersResponse> => {
      const response = await api.get<SipPeersResponse>('/api/sip-peers', params);
      return response.data as SipPeersResponse;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Fetch single SIP peer by ID
 */
export function useSipPeer(peerId: string) {
  return useQuery<SipPeerResponse>({
    queryKey: sipPeerQueryKeys.peer(peerId),
    queryFn: async () => {
      const response = await api.get<SipPeerResponse>(`/api/sip-peers/${peerId}`);
      return response.data as SipPeerResponse;
    },
    enabled: !!peerId,
  });
}

/**
 * Fetch SIP peers by type
 */
export function useSipPeersByType(type: 'carrier' | 'teams' | 'webrtc' | 'softphone' | 'pbx') {
  return useQuery<SipPeersResponse>({
    queryKey: ['sip-peers-by-type', type],
    queryFn: async () => {
      const response = await api.get<SipPeersResponse>(`/api/sip-peers/by-type/${type}`);
      return response.data as SipPeersResponse;
    },
  });
}

/**
 * Create SIP peer mutation
 * Uses Sip.PeerCreateInput - the input type that allows optional fields with defaults
 */
export function useCreateSipPeer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Sip.PeerCreateInput) => {
      const response = await api.post<SipPeerResponse>('/api/sip-peers', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
    },
  });
}

/**
 * Update SIP peer mutation
 * Uses Sip.PeerUpdateInput - all fields optional for partial updates
 */
export function useUpdateSipPeer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ peerId, data }: { peerId: string; data: Sip.PeerUpdateInput }) => {
      const response = await api.put<SipPeerResponse>(`/api/sip-peers/${peerId}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peer(variables.peerId) });
    },
  });
}

/**
 * Delete SIP peer mutation
 */
export function useDeleteSipPeer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (peerId: string) => {
      await api.delete(`/api/sip-peers/${peerId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
    },
  });
}

/**
 * Enable/disable SIP peer mutation
 */
export function useToggleSipPeer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ peerId, enabled }: { peerId: string; enabled: boolean }) => {
      await api.patch(`/api/sip-peers/${peerId}/enable`, { enabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sipPeerQueryKeys.peers });
    },
  });
}

/**
 * Test FQDN mutation
 */
export function useTestFqdn() {
  return useMutation({
    mutationFn: async ({ fqdn, sourceIp }: { fqdn: string; sourceIp?: string }) => {
      const response = await api.post('/api/sip-peers/test-fqdn', { fqdn, sourceIp });
      return response.data;
    },
  });
}

/**
 * Validate source IP mutation
 */
export function useValidateSourceIp() {
  return useMutation({
    mutationFn: async ({ sourceIp, peerId }: { sourceIp: string; peerId: string }) => {
      const response = await api.post('/api/sip-peers/validate-source-ip', { sourceIp, peerId });
      return response.data;
    },
  });
}
